import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import { HighchartsReact } from "highcharts-react-official";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import highchartsAccessibility from "highcharts/modules/accessibility";
import highchartsExporting from "highcharts/modules/exporting";
import highchartsOfflineExporting from "highcharts/modules/offline-exporting";
import { IntlContext } from "context";
import { exportCharts } from "helpers/highcharts/export";
import { createDiagramSeries } from "helpers/highcharts/getDiagramCase";
import produitDataFetcher from "dataProvider/Produit";
import usePromise from "helpers/usePromise";
import Loader from "ui/Loader";
import { matomoPush } from "../../Produit/helpers";

// Nécessaire pour ce type de graph
HighchartsMore(Highcharts);
highchartsAccessibility(Highcharts);
highchartsExporting(Highcharts);
highchartsOfflineExporting(Highcharts);

const ExportDiagram: React.FC<{ activeFilters: any }> = ({ activeFilters }) => {
    const intl = useContext(IntlContext);
    const [downloading, setDownloading] = useState<boolean>(false);
    const activeFiltersJson = JSON.stringify(activeFilters);
    const {isLoading, changePromiseParams, data} = usePromise(produitDataFetcher.stats, activeFilters);
    const chartsRef = useRef<Highcharts.Chart[]>([]);

    useEffect(() => {
        const filters = Object.assign(JSON.parse(activeFiltersJson), {filter_diagram: true});
        changePromiseParams(filters);
        matomoPush([
            ["setReferrerUrl", window.location.href],
            ["setCustomUrl", `${window.location.href}/composition_nutritionnelle`],
        ]);
    }, [activeFiltersJson, changePromiseParams]);


    const getSeries = useMemo(() => createDiagramSeries(activeFilters, data), [activeFilters, data]);

    if (isLoading || !data) {
        return <Loader message={intl.formatMessage("generic.loader.loading.label")}/>;
    }

    const totalResults = (): string => {
        const total = data?.pager?.total;
        if (typeof total !== "number") {
            return "";
        }
        if (total === 0) {
            return intl.formatMessage("produit.label.no_result");
        }
        return (
            `${total} ` +
            (total > 1 ? intl.formatMessage("produit.label.results") : intl.formatMessage("produit.label.result"))
        );
    };

    if (!data || data.length <= 0) {
        return (
            <div className="stats-container-top">
                <span className="results" id="no-result" >{totalResults()}</span>
            </div>
        );
    }


    type ChartProps = {
        chartOptions: Highcharts.Options;
        v: number;
    };

    const Chart = ({chartOptions, v}: ChartProps) => {
        return (
            <div className="custom-chart">
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                    containerProps={{style: {height: "100%", width: "100%"}}}
                    ref={(chart) => {
                        if(chart && chart !== undefined){
                            chartsRef.current[v] = chart.chart;
                        }
                    }}
                />
            </div>
        );
    };

    const GenerateDatable = (data: any) => {
        if (!data.data?.exportData) {
            return <></>;
        }
        return (
            <table className="stats-table-alimentaire">
                <thead className="row-group">
                <tr className="header-row">
                    <th className="header-cell">
                        {data.data.exportData?.header?.code ||
                            intl.formatMessage("offre_alimentaire.diagram.header.code")}
                    </th>
                    <th className="header-cell">
                        {data.data.exportData?.header?.label ||
                            intl.formatMessage("offre_alimentaire.diagram.header.label")}
                    </th>
                    <th className="header-cell">
                        {data.data.exportData?.header?.effectif ||
                            intl.formatMessage("offre_alimentaire.diagram.header.effectif")}
                    </th>
                </tr>
                </thead>
                <tbody>
                {data.data.exportData.labels.map((categorie: any, index: number) => {
                    const result = categorie.match(/^(.*)<br\/>n: (\d+)$/);
                    return (
                        <tr key={index}>
                            <td className="cell">{index + 1}</td>
                            <td className="cell">{result[1]}</td>
                            <td className="cell">{result[2]}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        );
    };

    const generateBoxPlots = (series: any[], t: number) => {
        return Object.values(series).map((configDiagram: Highcharts.Options, k: number) => {
            return (
                <div key={k}>
                    <Chart chartOptions={configDiagram} v={t + k}/>
                    <div className="highcharts-credits">
                        {intl.formatMessage("produit.link.credits", {date: new Date().toLocaleDateString("fr")})}
                    </div>
                    <GenerateDatable data={configDiagram}/>
                </div>
            );
        });
    };

    const generateOneOrManyBoxPlots = (series: any[]) => {
        if (Array.isArray(series[0])){
            let num = 0;
            return series.map((value:any[], index: number) => {
                const gen = generateBoxPlots(value, num);
                num += value.length;
                return gen;
            });
        }else {
            return generateBoxPlots(series, 0);
        }
    }

    const exportDiagram = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            event.preventDefault();
            setDownloading(true);
            matomoPush([["trackEvent", "Export outil de visualisation", "Composition nutritionnelle", JSON.stringify(activeFilters?.matomo), 1]]);

            const charts = chartsRef.current;
            setTimeout(() => {
                exportCharts(charts, getSeries, {
                    code: intl.formatMessage("offre_alimentaire.diagram.header.code"),
                    label: intl.formatMessage("offre_alimentaire.diagram.header.label"),
                    effectif: intl.formatMessage("offre_alimentaire.diagram.header.effectif"),
                });
            }, 500);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloading(false);
        }
    };

    const exportButton = (
        <div className="container--export">
            <button
                type="button"
                id="export-button"
                className="cta cta--secondary"
                title={intl.formatMessage("generic.export")}
                disabled={downloading}
                onClick={(e) => exportDiagram(e)}
            >
                {downloading
                    ? intl.formatMessage("generic.loader.loading.label")
                    : intl.formatMessage("generic.export")}
            </button>
        </div>
    );

    return (
        <>
            {exportButton}

            {generateOneOrManyBoxPlots(getSeries)}
        </>
    );
};

export default ExportDiagram;
